import { useMemo, useState } from 'react'
import { LiabilitySectionData } from '../../../dtos/applicationData'
import FileData from '../../../dtos/fileData'
import { useApplication } from '../../../context/application'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  FormContainer,
  FormItemContainer
} from '../../../components/application/defaultComponents'
import ApplicationPrebuiltFooter from '../../../components/application/PrebuiltBodyFooter'
import StepBullet from '../../../components/basic/StepBullet'
import OptionFile from '../../../components/application/formComponents/OptionFile'
import OptionReminder from '../../../components/application/formComponents/OptionReminder'
import OptionText from '../../../components/application/formComponents/OptionText'
import { ValidationError } from 'yup'
import { AttorneyPastLiabilitySchema } from 'src/yup/NewAppSchema'

type PastLiabilityData = LiabilitySectionData["pastLiability"]

const Main = () => {
  const { application, saveApplication } = useApplication()
  const [pastLiability, setPastLiability] = useState<PastLiabilityData>(
    () => !!application?.liabilitySection?.pastLiability ? { ...application.liabilitySection.pastLiability } : {}
  )
  const [showErrors, setShowErrors] = useState(false)

  

  const validateSteps = useMemo<{ [key: string]: boolean }>(() => {
    let validatedSteps: string[] = []
    
    try {
      AttorneyPastLiabilitySchema.validateSync(pastLiability, { abortEarly: false })
    } catch(err) {
      const { errors } = err as ValidationError
      
      validatedSteps = errors
    }
    
    return {
      step1: !validatedSteps.includes('step1'),
      step2: !validatedSteps.includes('step2'),
      step3: !validatedSteps.includes('step3'),
      step4: !validatedSteps.includes('step4'),
      step5: !validatedSteps.includes('step5'),
    }
  }, [AttorneyPastLiabilitySchema, pastLiability])

  const handleUploadFile = async (fileData: FileData[] | undefined, prop: 'canceledLiabilitiesFile' | 'grievanceComplainFile') => {
    if(!fileData) {
      setPastLiability(prev => ({ ...prev, [prop]: undefined }))
      
      try {
        await saveApplication({ 
          ...application,
          stoppedAt: '/insurance/past-liability',
          liabilitySection: { ...application.liabilitySection, pastLiability: {...pastLiability, [prop]: undefined}}
        }) 
      } catch(_) {}
    } else {
      setPastLiability(prev => ({ ...prev, [prop]: [...(prev?.[prop] ?? []), ...fileData] }))

      try {
        await saveApplication({ 
          ...application,
          stoppedAt: '/insurance/past-liability',
          liabilitySection: { ...application.liabilitySection, pastLiability: {...pastLiability, [prop]: [...(pastLiability?.[prop] ?? []), ...fileData]}}
        }) 
      } catch(_) {}
    }
  }

  const validatePage = () => Object.keys(validateSteps).every((key) => validateSteps[key] === true)

  const updatedApplicationData = () => ({ 
    ...application,
    currentPage: window.location.pathname,
    liabilitySection: { ...application.liabilitySection, pastLiability: pastLiability}
  })

  const navigateTo = (pastLiability?.hasMalpracticeClaim || pastLiability?.hasMalpracticeBasis) ?
    '/insurance/claims' :
    '/quote/type'

  const handleScrollInvalidPage = () => {
    if(!validatePage()) {
      for(let [key, isValid] of Object.entries(validateSteps)) {
        if(!isValid) {
          setShowErrors(true)
          document.getElementById(key)?.scrollIntoView({ behavior: 'smooth' })
          break
        }
      }
    }
  }
    
  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='75%'>
        <DefaultTitle>Let's look at any past liability</DefaultTitle>
        <FormContainer maxWidth='760px' style={{ marginTop: '90px' }}>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step1" isComplete={validateSteps.step1}>1</StepBullet>
            <OptionFile
              title='In the past 10 years, has your firm or any lawyer at your firm had liability insurance cancelled, declined, or not renewed?'
              fileHint='Attach a copy of cancellation, declination or norenewal notice(s)'
              showOptions={true}
              filesData={pastLiability?.canceledLiabilitiesFile}
              setFilesData={fileData => handleUploadFile(fileData, 'canceledLiabilitiesFile')}
              selectedOption={pastLiability?.hasCanceledLiabilities}
              setSelectedOption={option => setPastLiability(prev => ({ ...prev, hasCanceledLiabilities: option }))}
              isIncorrect={showErrors && !validateSteps.step1}
              errorCode='EC264'
            />
          </FormItemContainer>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step2" isComplete={validateSteps.step2}>2</StepBullet>
            <OptionReminder
              title='In the past 10 years, has your firm or any lawyer at your firm been the subject of a legal malpractice claim?'
              reminderTitle='Additional Information Required'
              reminderText='You will need to fill out additional claim information on the next page regarding this answer.'
              showOptions={true}
              selectedOption={pastLiability?.hasMalpracticeClaim}
              setSelectedOption={option => setPastLiability(prev => ({...prev, hasMalpracticeClaim: option }))}
              isIncorrect={showErrors && !validateSteps.step2}
              errorCode='EC265'
            />
          </FormItemContainer>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step3" isComplete={validateSteps.step3}>3</StepBullet>
            <OptionReminder
              title='Does any firm member or non-attorney employee of your firm (including predecessor firms) know of any circumstance, act, error, or omission that might form the basis of a legal malpractice claim against your firm? Or any attorney in your firm?'
              reminderTitle='Additional Information Required'
              reminderText='You will need to fill out additional claim information on the next page regarding this answer.'
              showOptions={true}
              selectedOption={pastLiability?.hasMalpracticeBasis}
              setSelectedOption={option => setPastLiability(prev => ({...prev, hasMalpracticeBasis: option }))}
              isIncorrect={showErrors && !validateSteps.step3}
              errorCode='EC266'
            />
          </FormItemContainer>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step4" isComplete={validateSteps.step4}>4</StepBullet>
            <OptionFile
              title='Has your firm or any lawyer at your firm been the subject of a grievance complaint in the past five years?'
              fileHint='Attach a copy of grievance, description of circumstances and current status. If dismissed, please attach a copy of the dismissal letter.'
              showOptions={true}
              filesData={pastLiability?.grievanceComplainFile}
              setFilesData={fileData => handleUploadFile(fileData, 'grievanceComplainFile')}
              selectedOption={pastLiability?.hasGrievanceComplain}
              setSelectedOption={option => setPastLiability(prev => ({ ...prev, hasGrievanceComplain: option }))}
              isIncorrect={showErrors && !validateSteps.step4}
              errorCode='EC267'
            />
          </FormItemContainer>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step5" isComplete={validateSteps.step5}>5</StepBullet>
            <OptionText
              title='Has your firm or any lawyer at your firm ever voluntarily accepted discipline or been refused admission to the bar, been reprimanded (privately or publicly), suspended from the practice of law, disbarred, received a probationary license, or otherwise disciplined by any disciplinary or licensing authority?'
              textHint='Please provide details.'
              showOptions={true}
              text={pastLiability?.disciplinedActionDetails}
              setText={text => setPastLiability(prev => ({ ...prev, disciplinedActionDetails: text ?? undefined }))}
              selectedOption={pastLiability?.hasDisciplinedAction}
              setSelectedOption={option => setPastLiability(prev => ({ ...prev, hasDisciplinedAction: option }))}
              isIncorrect={showErrors && !validateSteps.step5}
              errorCodes={['EC268', 'EC269']} 
            />
          </FormItemContainer>
        </FormContainer>
      </ApplicationBodyContent>
      <ApplicationPrebuiltFooter
        goBackRoute="/insurance/last-insurances"
        isFormValid={validatePage()}
        nextRoute={navigateTo}
        updatedApplication={updatedApplicationData()}
        beforeSaveContinueClick={handleScrollInvalidPage}
      />
    </ApplicationBodyContainer>
  )
}

export default Main
